import React, { FunctionComponent } from "react";
import { graphql } from "gatsby";
import { FluidObject } from "gatsby-image";
import { BlogPost } from "../../components/blogPost";
import { SEO } from "../../components/seo";
import { PostType, TopicKey } from "../../topics";
import {
  PostSnippetNode,
  ProjectPartNode,
  ProjectSummaryNode,
  toPostSnippet,
  toProjectSummaries,
} from "../../data/posts";

interface QueryData {
  markdownRemark: {
    html: string;
    headings: { id: string; value: string }[];
    fields: {
      slug: string;
      readingTime: number;
    };
    frontmatter: {
      title: string;
      tags: string[];
      img: {
        childImageSharp: { fluid: FluidObject };
      };
      imgAlt: string;
      publishedDate: string;
      description: string;
      topic: TopicKey;
      type: PostType;
      projects: { slug: string }[] | null;
    };
  };
  related: { nodes: PostSnippetNode[] };
  topicPosts: { totalCount: number };
  projects: { nodes: ProjectSummaryNode[] };
  projectParts: { nodes: ProjectPartNode[] };
}

export const pageQuery = graphql`
  query BlogPostByID($id: String!, $topic: String!, $projectSlugs: [String]) {
    markdownRemark(id: { eq: $id }) {
      id
      html
      headings(depth: h2) {
        id
        value
      }
      fields {
        slug
        readingTime
      }
      frontmatter {
        title
        description
        tags
        topic
        type
        projects {
          slug
        }
        img {
          childImageSharp {
            fluid(maxWidth: 2400, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        imgAlt
        publishedDate
      }
    }
    related: allMarkdownRemark(
      limit: 3
      sort: { fields: [frontmatter___publishedDate], order: DESC }
      filter: {
        fields: { collection: { eq: "blog" } }
        frontmatter: { topic: { eq: $topic } }
        id: { ne: $id }
      }
    ) {
      nodes {
        ...PostSnippet
      }
    }
    topicPosts: allMarkdownRemark(
      filter: {
        fields: { collection: { eq: "blog" } }
        frontmatter: { topic: { eq: $topic } }
      }
    ) {
      totalCount
    }
    projects: allMarkdownRemark(
      filter: {
        fields: { collection: { eq: "projects" } }
        frontmatter: { slug: { in: $projectSlugs } }
      }
    ) {
      nodes {
        ...ProjectSummary
      }
    }
    projectParts: allMarkdownRemark(
      filter: {
        fields: { collection: { eq: "blog" } }
        frontmatter: {
          projects: { elemMatch: { slug: { in: $projectSlugs } } }
        }
      }
    ) {
      nodes {
        ...ProjectPart
      }
    }
  }
`;

interface Page {
  data: QueryData;
}

export const Page: FunctionComponent<Page> = ({ data }) => {
  const {
    markdownRemark: {
      fields: { slug, readingTime },
      html,
      headings,
      frontmatter: {
        title,
        tags,
        imgAlt,
        description,
        img: {
          childImageSharp: { fluid: img },
        },
        publishedDate,
        topic,
        type,
        projects: projectRefs,
      },
    },
  } = data;

  // Keep the frontmatter order so the first listed project drives the "Part X of Y" sticker.
  const projectOrder = (projectRefs || []).map((ref) => ref.slug);
  const projects = toProjectSummaries(
    data.projects.nodes,
    data.projectParts.nodes
  ).sort((a, b) => projectOrder.indexOf(a.slug) - projectOrder.indexOf(b.slug));

  return (
    <>
      <SEO
        title={title}
        image={img.src}
        description={description}
        type="article"
      />
      <BlogPost
        title={title}
        description={description}
        slug={slug}
        tags={tags}
        img={img}
        imgAlt={imgAlt}
        publishedDate={new Date(publishedDate)}
        readingTime={readingTime}
        topic={topic}
        type={type}
        headings={headings.map(({ id, value }) => ({ id, label: value }))}
        projects={projects}
        related={data.related.nodes.map(toPostSnippet)}
        topicPostCount={data.topicPosts.totalCount}
        html={html}
      />
    </>
  );
};

export default Page;
